import Environment from '../lib/Environment';
import XhrRequestHandler from '../lib/XhrRequestHandler';
import XhrRequestCache from '../lib/XhrRequestCache';
import XhrErrorListener from '../lib/XhrErrorListener';
import CryptoJS from 'crypto-js';
import { Club, ClubJSON } from './Club/Club';
import { Federacion, FederacionJSON } from './Federacion/Federacion';
import { Provincia, ProvinciaJSON } from './Provincia/Provincia';
import { Categoria, CategoriaJSON } from './Categoria/Categoria';
import {
  AperturaAptoFisicoXTemporada,
  AperturaAptoFisicoXTemporadaJSON,
} from './AperturaAptoFisicoXTemporada/AperturaAptoFisicoXTemporada';
import { AptoFisico } from './AptoFisico/AptoFisico';
import { DocumentoTipoJSON, DocumentoTipo } from './DocumentoTipo/DocumentoTipo';
import { JugadorJSON, Jugador } from './Jugador/Jugador';
import { CategoriaSystem } from './Categoria/CategoriaSystem';
import { ClubSystem } from './Club/ClubSystem';
import { RegistroEntrenadorSystem } from './RegistroEntrenador/RegistroEntrenadorSystem';
import { FederacionSystem } from './Federacion/FederacionSystem';
import { RegistroEntrenador } from './RegistroEntrenador/RegistroEntrenador';
import { ProvinciaSystem } from './Provincia/ProvinciaSystem';

function decrypt(encryptedText: string) {
  const env = new Environment();
  const parts = encryptedText.split(':');
  const iv = CryptoJS.enc.Hex.parse(parts[0]);
  const encrypted = CryptoJS.enc.Hex.parse(parts[1]);
  const key = CryptoJS.enc.Hex.parse(CryptoJS.enc.Base64.parse(env.passphrase()).toString(CryptoJS.enc.Hex));
  // @ts-ignore
  const decrypted = CryptoJS.AES.decrypt({ ciphertext: encrypted }, key, {
    iv: iv,
    mode: CryptoJS.mode.CTR,
    padding: CryptoJS.pad.NoPadding,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
}

class SystemClient {
  private categoriaSystem: CategoriaSystem;
  private clubSystem: ClubSystem;
  private registroEntrenadorSystem: RegistroEntrenadorSystem;
  private federacionSystem: FederacionSystem;
  private provinciaSystem: ProvinciaSystem;
  private static readonly REGISTRO_ENTRENADORES = '/registroentrenadores';

  private constructor(private requestHandler: XhrRequestHandler, private environment: Environment) {
    this.categoriaSystem = new CategoriaSystem(this.requestHandler);
    this.clubSystem = new ClubSystem(this.requestHandler);
    this.registroEntrenadorSystem = new RegistroEntrenadorSystem(this.requestHandler);
    this.federacionSystem = new FederacionSystem(this.requestHandler);
    this.provinciaSystem = new ProvinciaSystem(this.requestHandler);
  }

  static start(environment: Environment) {
    const requestHandler = new XhrRequestHandler(environment, XhrRequestCache.with([]));

    return new this(requestHandler, environment);
  }

  getEnvironment() {
    return this.environment;
  }

  unsubscribe(aListener: XhrErrorListener) {
    return this.requestHandler.unsubscribe(aListener);
  }

  subscribe(aListener: XhrErrorListener) {
    return this.requestHandler.subscribe(aListener);
  }

  async clubes() {
    const clubes = await this.requestHandler.get<ClubJSON[]>(`${SystemClient.REGISTRO_ENTRENADORES}/clubes`);
    return clubes.map((c) => Club.fromJSON(c));
  }

  async documentoTipos() {
    const tipos = await this.requestHandler.get<DocumentoTipoJSON[]>(`${SystemClient.REGISTRO_ENTRENADORES}/tipos-documento`);
    return tipos.map((c) => DocumentoTipo.fromJSON(c));
  }

  async federacionForDropdown(): Promise<Federacion[]> {
    const data = await this.requestHandler.get<FederacionJSON[]>(
      `${SystemClient.REGISTRO_ENTRENADORES}/federaciones-for-dropdown`
    );
    return data.map((entity) => Federacion.fromJSON(entity));
  }

  async getProvinciasForDropdown(): Promise<Provincia[]> {
    const data = await this.requestHandler.get<ProvinciaJSON[]>(
      `${SystemClient.REGISTRO_ENTRENADORES}/provincias-for-dropdown`
    );
    return data.map((entity) => Provincia.fromJSON(entity));
  }

  async getClubesForDropdown(): Promise<Club[]> {
    const data = await this.requestHandler.get<ClubJSON[]>(`${SystemClient.REGISTRO_ENTRENADORES}/clubes-for-dropdown`);
    return data.map((entity) => Club.fromJSON(entity));
  }

  async getCategoriasListForDropdown(): Promise<Categoria[]> {
    const data = await this.requestHandler.get<CategoriaJSON[]>(
      `${SystemClient.REGISTRO_ENTRENADORES}/categorias-for-dropdown`
    );
    return data.map((entity) => Categoria.fromJSON(entity));
  }

  // async aperturaVigente() {
  //   const apertura = await this.requestHandler.get<AperturaAptoFisicoXTemporadaJSON>(
  //     `${SystemClient.REGISTRO_ENTRENADORES}/apertura-vigente`
  //   );
  //   return apertura ? AperturaAptoFisicoXTemporada.fromJSON(apertura) : apertura;
  // }

  // async save(registroEntrenador: RegistroEntrenador, fileDocumento: File | null, fileSelfie: File | null, token: string) {
  //   var bodyFormData = new FormData();
  //   if (fileDocumento) bodyFormData.append('documento', fileDocumento);
  //   if (fileSelfie) bodyFormData.append('selfie', fileSelfie);
  //   bodyFormData.append('registroEntrenador', JSON.stringify(registroEntrenador.toJSON()));
  //   bodyFormData.append('token', token);

  //   const data = await this.requestHandler.postWithFormData<unknown>(`registroentrenadors/save`, bodyFormData);
  //   return data;
  // }

  getCategoriaSystem() {
    return this.categoriaSystem;
  }

  getClubSystem() {
    return this.clubSystem;
  }

  getRegistroEntrenadorSystem() {
    return this.registroEntrenadorSystem;
  }

  getFederacionSystem() {
    return this.federacionSystem;
  }

  getProvinciaSystem() {
    return this.provinciaSystem;
  }
}

export default SystemClient;
